import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Space, Tooltip} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {
  StyledBudgetPreview,
  StyledBudgetPreviewOverBudgetTag,
  StyledBudgetPreviewMatchedTag,
  StyledBudgetPreviewMatchedTagTooltip
} from './StyledBudgetPreview';
import BudgetPreviewLegend from '../BudgetPreviewLegend';
import UnlinkBudgetButton from '../../BudgetActiveDetailsPage/UnlinkBudgetButton';
import IconWrapper from '../../../IconWrapper';
import OverflowProgress from '../../../OverflowProgress';
import {CheckIcon, CloseIcon, StarsIcon} from '../../../../icons';
import {budgetHelpers, invoicesHelpers} from '../../../../utils/helpers';
import {colorsStyles} from '../../../../utils/styles';

const budgetSpendingKeys = {
  INVOICED: 'invoiced',
  REMAINING: 'remaining',
  SPENT: 'spend',
}

const budgetSpendingData = {
  [budgetSpendingKeys.INVOICED]: {
    amountKey: 'invoice',
    color: '#B37FEB',
    labelKey: 'thisInvoice',
  },
  [budgetSpendingKeys.SPENT]: {
    amountKey: 'spent',
    color: '#515A6B',
    labelKey: 'spent',
  },
  [budgetSpendingKeys.REMAINING]: {
    amountKey: 'remaining',
    color: colorsStyles.neutral_3,
    labelKey: 'remaining',
  },
}

const calculateSpent = ({budget, invoice} = {}) => {
  const spent = budget?.spent_amount || 0 + budget?.invoiced_amount || 0;
  const isUnassigned = invoicesHelpers.isUnassignedInvoice(invoice);
  const invoiceSpent = isUnassigned ? invoice?.amount || 0 : 0;
  const totalSpent = spent + invoiceSpent;
  return { invoiceSpent, totalSpent, spent };
}

const BudgetPreview = ({
  budget,
  invoice,
  onUnlink,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'budgets']);

  const spending = useMemo(() => {
    if (!budget || !invoice) return {  };

    const { invoiceSpent, totalSpent, spent } = calculateSpent({ budget, invoice });
    const utilizationPercentage = ((spent + invoiceSpent) / (budget?.limit_amount || 1)) * 100;

    return {
      invoice: invoiceSpent,
      spent: spent,
      remaining: (budget?.limit_amount || 0) - totalSpent,
      overBudget: utilizationPercentage > 100 ? Number(utilizationPercentage - 100).toFixed(2) : 0,
      utilizationPercentage
    };
  }, [budget, invoice]);

  const { progressProps } = useMemo(() => {
    if (!budget || !invoice) return { progressProps: {} };

    const { totalSpent, spent } = calculateSpent({ budget, invoice });
    const budgetLimit = budget?.limit_amount || 0;
    return budgetHelpers.calculateBudgetProgress({
      totalSpent,
      budgetLimit,
      spent,
    });
  }, [budget, invoice]);

  // Determine match status between invoice and budget/PO
  const matchResult = useMemo(() => {
    if (!invoice || !budget) return null;

    const invoiceAmount = invoice?.amount || 0;
    const budgetAmount = budget?.limit_amount || 0;
    const invoiceVendorId = invoice?.vendor_local_id;

    // Amount match: equal or within 5% of invoice amount
    let amountMatched = false;
    if (invoiceAmount != null && budgetAmount != null) {
      const diff = Math.abs(invoiceAmount - budgetAmount);
      const threshold = Math.abs(invoiceAmount) * 0.05; // 5% of invoice amount
      if (diff <= threshold) amountMatched = true;
    }

    const data = [
      { key: 'vendor', success: invoiceVendorId && invoiceVendorId === budget?.vendor_id },
      { key: 'date', success: false },
      { key: 'reference', success: false },
      { key: 'amount', success: amountMatched },
    ]

    const status = data.filter(i => i.success).length >= 3 ? 'recommended' : 'review';

    return { data, status };
  }, [budget, invoice]);

  const renderTitle = (key) => {
    const data = budgetSpendingData[key];
    if (!data) return null;
    const amount = spending?.[data.amountKey] || 0;
    if (!amount) return null;

    return <BudgetPreviewLegend amount={amount} color={data?.color} title={t(data?.labelKey)} />;
  }

  const renderMatchTag = () => {
    if (!matchResult) return null;

    const tooltipContent = (
      <StyledBudgetPreviewMatchedTagTooltip>
        {matchResult.data.map((item, index) => (
          <div key={index} className='d-flex justify-space-between align-items-center mb-8'>
            <span>{t(item.key)}</span>
            {item.success
              ? <IconWrapper size={16} color={colorsStyles.functional_success}><CheckIcon /></IconWrapper>
              : <IconWrapper size={16} color={colorsStyles.functional_danger}><CloseIcon /></IconWrapper>
            }
          </div>
        ))}
      </StyledBudgetPreviewMatchedTagTooltip>
    );

    return (
      <StyledBudgetPreviewMatchedTag status={matchResult.status} size='small' align='center'>
        <StarsIcon />
        {t(matchResult.status)}
        <Tooltip title={tooltipContent} placement='bottom'>
          <InfoCircleOutlined style={{ color:colorsStyles.neutral_6 }} />
        </Tooltip>
      </StyledBudgetPreviewMatchedTag>
    );
  };

  return (
    <StyledBudgetPreview size='middle' direction='vertical' {...rest}>
      <div className='d-flex justify-space-between align-items-center'>
        <Space size='middle' className='d-flex d-flex-child'>
          {budgetHelpers.getBudgetNameWithLink(budget)}
          {renderMatchTag()}
        </Space>
        <UnlinkBudgetButton onOk={onUnlink} />
      </div>
      <Space direction='vertical' size='small'>
        <OverflowProgress
          {...progressProps}
          hidePercent={true}
          percent={spending.overBudget ? spending.utilizationPercentage : progressProps.percent}
          strokeColor={budgetSpendingData[budgetSpendingKeys.INVOICED].color}
          size={{ height: 6 }}
          showInfo={false}
          trailColor={colorsStyles.neutral_3}
          strokeLinecap='butt'
        />
        <Space size='small' wrap>
          {renderTitle(budgetSpendingKeys.SPENT)}
          {renderTitle(budgetSpendingKeys.INVOICED)}
          {renderTitle(budgetSpendingKeys.REMAINING)}
          {Boolean(spending.overBudget) && (
            <StyledBudgetPreviewOverBudgetTag>
              {t('overbudget')} {spending.overBudget}%
            </StyledBudgetPreviewOverBudgetTag>
          )}
        </Space>
      </Space>
    </StyledBudgetPreview>
  );
};

BudgetPreview.propTypes = {
  invoice: PropTypes.shape({
    budget_request_id: PropTypes.string,
    id: PropTypes.string,
  }),
  onUnlink: PropTypes.func,
};

export default BudgetPreview;
